"use client"

import { useState } from "react"

interface IntroTourCardProps {
  onStartTour: (mode: "quick" | "full") => void
  onClose: () => void
  isMobile: boolean
}

export function IntroTourCard({ onStartTour, onClose, isMobile }: IntroTourCardProps) {
  const [activeTab, setActiveTab] = useState<"quick" | "full">("quick")

  if (isMobile) {
    // Mobile: full-width card at bottom
    return (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/60 z-59" onClick={onClose} />

        {/* Card at bottom. max-h + scroll keeps the Start button reachable on
            short landscape phone viewports, where the card used to overflow. */}
        <div className="fixed bottom-0 left-0 right-0 z-60 max-h-dvh overflow-y-auto bg-white text-black">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-lg hover:bg-black/10 transition-colors z-10"
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Header image placeholder */}
          <div className="w-full h-32 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <img
              src="/images/pelf-logo.png"
              alt="PELF"
              className="h-20 object-contain"
              style={{ imageRendering: "pixelated" }}
            />
          </div>

          <div className="p-4 pb-8">
            <h2 className="text-xl font-bold mb-4">Welcome to PELF!</h2>

            {/* Split toggle button */}
            <div className="flex mb-4 rounded-lg overflow-hidden border border-black">
              <button
                onClick={() => setActiveTab("quick")}
                className={`flex-1 py-2 px-3 text-sm font-medium transition-colors ${
                  activeTab === "quick"
                    ? "bg-black text-white"
                    : "bg-white text-black hover:bg-gray-100"
                }`}
              >
                Quick Start
              </button>
              <button
                onClick={() => setActiveTab("full")}
                className={`flex-1 py-2 px-3 text-sm font-medium transition-colors border-l border-black ${
                  activeTab === "full"
                    ? "bg-black text-white"
                    : "bg-white text-black hover:bg-gray-100"
                }`}
              >
                Full Tour
              </button>
            </div>

            {/* Tab content */}
            <div className="text-sm text-gray-600 mb-4 min-h-[2.5em]">
              {activeTab === "quick" ? (
                <p>Learn the basics in 6 quick steps. Perfect for jumping right in!</p>
              ) : (
                <p>Explore all features in 9 detailed steps. Great for mastering the game!</p>
              )}
            </div>

            {/* Start button */}
            <button
              onClick={() => onStartTour(activeTab)}
              className="w-full py-3 bg-black hover:bg-gray-800 text-white rounded-lg font-bold text-lg transition-colors"
            >
              Start Tour
            </button>
          </div>
        </div>
      </>
    )
  }

  // Desktop: centered modal
  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 z-59" onClick={onClose} />

      {/* Centered modal */}
      <div className="fixed z-60 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md max-h-dvh p-2">
        {/* max-h + scroll keeps the Start button reachable on short viewports */}
        <div className="bg-white text-black rounded-xl shadow-2xl overflow-y-auto max-h-full">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-lg hover:bg-black/10 transition-colors z-10"
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Header image placeholder */}
          <div className="w-full h-40 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <img
              src="/images/pelf-logo.png"
              alt="PELF"
              className="h-24 object-contain"
              style={{ imageRendering: "pixelated" }}
            />
          </div>

          <div className="p-6">
            <h2 className="text-2xl font-bold mb-6 text-center">Welcome to PELF!</h2>

            {/* Split toggle button */}
            <div className="flex mb-4 rounded-lg overflow-hidden border border-black">
              <button
                onClick={() => setActiveTab("quick")}
                className={`flex-1 py-2.5 px-4 font-medium transition-colors ${
                  activeTab === "quick"
                    ? "bg-black text-white"
                    : "bg-white text-black hover:bg-gray-100"
                }`}
              >
                Quick Start
              </button>
              <button
                onClick={() => setActiveTab("full")}
                className={`flex-1 py-2.5 px-4 font-medium transition-colors border-l border-black ${
                  activeTab === "full"
                    ? "bg-black text-white"
                    : "bg-white text-black hover:bg-gray-100"
                }`}
              >
                Full Tour
              </button>
            </div>

            {/* Tab content */}
            <div className="text-gray-600 mb-6 text-center min-h-[3em]">
              {activeTab === "quick" ? (
                <p>Learn the basics in 6 quick steps. Perfect for jumping right in!</p>
              ) : (
                <p>Explore all features in 9 detailed steps. Great for mastering the game!</p>
              )}
            </div>

            {/* Start button */}
            <button
              onClick={() => onStartTour(activeTab)}
              className="w-full py-3 bg-black hover:bg-gray-800 text-white rounded-xl font-bold text-lg transition-colors"
            >
              Start Tour
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
